import clsx from 'clsx';
import React from 'react';
import { useParams } from 'react-router';

import Spinner from '@/src/components/common/Elements/Spinner/Spinner';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { Authorization } from '@/src/lib/authorization/authorization';

import CopyEmailUrl from './CopyEmailUrl/CopyEmailUrl';
import CreateTask from './CreateTask/CreateTask';
import DeleteEmail from './DeleteEmail/DeleteEmail';
import MarkMiscellaneous from './MarkMiscellaneous/MarkMiscellaneous';
import MarkReadEmail from './MarkReadEmail/MarkReadEmail';
import PermanentDeleteEmail from './PermanentDeleteEmail/PermanentDeleteEmail';
import PrintEmail from './PrintEmail/PrintEmail';
import RestoreEmail from './RestoreEmail/RestoreEmail';
import TagEmailWorkstream from './TagEmailWorkstream/TagEmailWorkstream';
import ToggleSidePanel from './ToggleSidePanel/ToggleSidePanel';

import EllipsisActionsContainer from '../../../../../../Elements/EllipsisActionsContainer/EllipsisActionsContainer';
import SidebarItem from '../../../../Common/SidebarEntryPanel/SidebarItem/SidebarItem';
import SidebarSection from '../../../../Common/SidebarEntryPanel/SidebarSection/SidebarSection';
import SidebarEntryPanel from '../../../../FileEntryViewer/components/SidebarEntryPanel/SidebarEntryPanel';
import { useEmailThreadEntryViewer } from '../../../store/emailThreadEntryViewer.store';

export type SidebarSettings = {
  isDeleteEnabled: boolean;
};

type SidebarActionsProps = {
  sidebarSettings: SidebarSettings;
};

const SidebarActions = ({ sidebarSettings }: SidebarActionsProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedEmailEntry } = useEmailThreadEntryViewer();
  const { isTrash: isDeleted } = useInboxSearchParams();

  if (!selectedEmailEntry) {
    return null;
  }

  const isMainSectionEnabled =
    (selectedEmailEntry.permissions.createTask || selectedEmailEntry.permissions.misc) &&
    !isDeleted;
  const isEllipsisEnabled =
    (selectedEmailEntry.permissions.read || selectedEmailEntry.permissions.tagWorkstream) &&
    !isDeleted;

  return (
    <React.Fragment>
      {selectedEmailEntry.shouldWaitSocketUpdate && <Spinner />}
      <SidebarEntryPanel
        className={clsx(
          'tw-ml-[10px]',
          '!tw-border-solid !tw-border-[1px] !tw-border-cfo-grey-500',
          'tw-rounded-[5px]'
        )}
      >
        <Authorization policyCheck={isMainSectionEnabled}>
          <Authorization policyCheck={selectedEmailEntry.permissions.createTask}>
            <SidebarItem>
              <CreateTask />
            </SidebarItem>
          </Authorization>
          <Authorization policyCheck={selectedEmailEntry.permissions.misc}>
            <SidebarItem>
              <MarkMiscellaneous
                emailId={selectedEmailEntry.id}
                inboxEntryId={selectedEmailEntry.inboxEntryId}
                isMisc={selectedEmailEntry.isMisc}
              />
            </SidebarItem>
          </Authorization>
        </Authorization>

        <Authorization policyCheck={!isDeleted}>
          <SidebarSection className={clsx('!tw-px-[5px] !tw-py-0')}>
            <Authorization policyCheck={selectedEmailEntry.permissions.copyEmailUrl}>
              <SidebarItem>
                <CopyEmailUrl selectedEmailEntry={selectedEmailEntry} />
              </SidebarItem>
            </Authorization>

            <Authorization policyCheck={selectedEmailEntry.permissions.print}>
              <SidebarItem>
                <PrintEmail projectId={projectId} target="_blank" />
              </SidebarItem>
            </Authorization>

            <Authorization policyCheck={selectedEmailEntry.permissions.info}>
              <SidebarItem>
                <ToggleSidePanel />
              </SidebarItem>
            </Authorization>

            <SidebarItem>
              <EllipsisActionsContainer disabled={!isEllipsisEnabled} isActive={''}>
                <Authorization policyCheck={selectedEmailEntry.permissions.read}>
                  <MarkReadEmail
                    inboxEntryId={selectedEmailEntry.inboxEntryId}
                    emailId={selectedEmailEntry.id}
                    isRead={selectedEmailEntry.isRead}
                  />
                </Authorization>
                <Authorization policyCheck={selectedEmailEntry.permissions.tagWorkstream}>
                  <TagEmailWorkstream selectedEmailEntry={selectedEmailEntry} />
                </Authorization>
              </EllipsisActionsContainer>
            </SidebarItem>
          </SidebarSection>
        </Authorization>

        {/* <Authorization policyCheck={canDelete}> */}
        {!isDeleted && (
          <Authorization policyCheck={selectedEmailEntry.permissions.trash}>
            <SidebarSection>
              <SidebarItem>
                <DeleteEmail
                  selectedEmailEntry={selectedEmailEntry}
                  enabled={sidebarSettings.isDeleteEnabled}
                />
              </SidebarItem>
            </SidebarSection>
          </Authorization>
        )}
        {isDeleted && (
          <React.Fragment>
            <Authorization policyCheck={selectedEmailEntry.permissions.restore}>
              <SidebarSection>
                <SidebarItem>
                  <RestoreEmail
                    selectedEmailEntry={selectedEmailEntry}
                    enabled={sidebarSettings.isDeleteEnabled}
                  />
                </SidebarItem>
              </SidebarSection>
            </Authorization>
            <Authorization policyCheck={selectedEmailEntry.permissions.deletePermanent}>
              <SidebarSection>
                <SidebarItem>
                  <PermanentDeleteEmail
                    selectedEmailEntry={selectedEmailEntry}
                    enabled={sidebarSettings.isDeleteEnabled}
                  />
                </SidebarItem>
              </SidebarSection>
            </Authorization>
          </React.Fragment>
        )}
        {/* </Authorization> */}
      </SidebarEntryPanel>
    </React.Fragment>
  );
};

export default SidebarActions;
